'use client';
import React, { useEffect, useRef } from 'react';
import { Step, StepContent, Stepper } from '@mui/material';
import {
    CustomisedLinearConnector,
    LinearStepperLabel,
    StepperButtonRow,
    StepperContainer,
    StepperContent,
    StyledForm
} from '../styles';
import { FilledButton, OutlinedButton } from '../../buttons';
import { useTranslations } from 'next-intl';
import { useBreakpoint, useIsSessionExpired, useTheme } from '../../../hooks';
import { Breakpoint, isMedium } from '../../../types';
import { Center, Column, LabelMedium } from '../../../styles';
import { StepDetails } from '../types';
import { userSignOut } from '../../../auth/authSession';

type Props = {
    activeStep: number;
    steps: StepDetails[];
    onChangeStep: (stepIndex: number) => void;
    onSubmit: () => void;
    isContinueDisabled?: boolean;
    submitButtonLabel?: string;
    backButtonLabel?: string;
    onBack?: () => void;
    longerConnectorLines?: boolean;
};

const LinearStepper = ({
    activeStep,
    steps,
    onChangeStep,
    onSubmit,
    onBack,
    isContinueDisabled,
    submitButtonLabel,
    backButtonLabel,
    longerConnectorLines
}: Props) => {
    const t = useTranslations('dialog');
    const breakpoint = useBreakpoint();
    const { colors, spacing } = useTheme();

    const isSessionExpired = useIsSessionExpired();

    const contentContainerRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const isExpanded = breakpoint === Breakpoint.Expanded;

    useEffect(() => {
        const form = formRef.current;

        // Reset scroll position after changing steps
        if (!!form?.scrollTop) form.current?.scrollTo(0, 0);
    }, [activeStep]);

    const handleNext = () => {
        if (isSessionExpired) {
            userSignOut();
        } else {
            const nextStep = activeStep + 1;
            onChangeStep(nextStep);
        }
    };

    const handleBack = () => {
        if (onBack) onBack();
        else {
            const backStep = activeStep - 1;
            onChangeStep(backStep);
        }
    };

    const handleSubmit = () => {
        if (isSessionExpired) {
            userSignOut();
        } else {
            onSubmit();
        }
    };

    const isLastStep = activeStep === steps.length - 1;
    const isFirstStep = activeStep === 0;
    const hideContinueButton = submitButtonLabel === undefined;

    return (
        <StepperContainer isExpanded={isExpanded}>
            <Stepper
                activeStep={activeStep}
                orientation={isExpanded ? 'vertical' : 'horizontal'}
                role={'navigation'}
                connector={
                    <CustomisedLinearConnector
                        breakpoint={breakpoint}
                        longerConnectorLines={longerConnectorLines}
                    />
                }
                // We need to inline style here to be able to pass the custom connector to the stepper. This does not work with a styled component stepper.
                sx={{
                    height: 'fit-content',
                    width: isExpanded ? '301px' : 'auto',
                    marginRight: isExpanded ? spacing(3) : spacing(1),
                    marginLeft: isExpanded ? 0 : spacing(1),
                    my: spacing(3)
                }}
            >
                {steps.map(({ label, description }, i) => (
                    <Step key={label}>
                        <LinearStepperLabel
                            optional={
                                activeStep !== i && isExpanded ? (
                                    <LabelMedium
                                        sx={{
                                            color:
                                                activeStep > i
                                                    ? colors.onBackground
                                                    : colors.onSurfaceVariant
                                        }}
                                    >
                                        {description}
                                    </LabelMedium>
                                ) : null
                            }
                        >
                            {isExpanded && label}
                        </LinearStepperLabel>
                        {description && isExpanded ? (
                            <StepContent slotProps={{ transition: { unmountOnExit: false } }}>
                                <LabelMedium>{description}</LabelMedium>
                            </StepContent>
                        ) : null}
                    </Step>
                ))}
            </Stepper>
            <Center>
                <StepperContent isMedium={isMedium(breakpoint)}>
                    <StyledForm ref={formRef} isExpanded={isExpanded}>
                        <Column gap={3} ref={contentContainerRef}>
                            {steps[activeStep]?.content}
                        </Column>
                    </StyledForm>

                    <StepperButtonRow
                        hasBorder={true}
                        sx={{
                            justifyContent: isFirstStep
                                ? 'flex-end'
                                : hideContinueButton
                                  ? 'center'
                                  : 'space-between'
                        }}
                    >
                        <OutlinedButton
                            aria-label={`${t('back')} Button`}
                            disabled={isFirstStep}
                            onClick={handleBack}
                            label={backButtonLabel ?? t('back')}
                            sx={{ display: isFirstStep ? 'none' : 'flex' }}
                        />
                        {submitButtonLabel && (
                            <FilledButton
                                aria-label={`${isLastStep ? submitButtonLabel : t('continue')} Button`}
                                onClick={isLastStep ? handleSubmit : handleNext}
                                label={isLastStep ? submitButtonLabel : t('continue')}
                                disabled={isContinueDisabled}
                            />
                        )}
                    </StepperButtonRow>
                </StepperContent>
            </Center>
        </StepperContainer>
    );
};

export { LinearStepper };
